import { useState } from 'react';
import { PropertyTitleOwnerOutput } from '@graphql/index';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Columns } from '@spark-web/columns';
import { Heading } from '@spark-web/heading';
import { DocumentTextIcon, ExclamationIcon, InformationCircleIcon } from '@spark-web/icon';
import { RadioCard, RadioGroup } from '@spark-web/radio';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import EligibilityStepHeader from './EligibilityStepHeader';

type TitleOwnerResultsProps = {
  owners: PropertyTitleOwnerOutput[];
  applicantName: string;
  productName: string;
  onConfirm: () => void;
  onTitleDisputed: () => void;
  onSelectionChange?: (selection: 'yes' | 'no') => void;
  isSubmitting?: boolean;
};

function getInitials(fullName: string): string {
  return fullName
    .split(' ')
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? '')
    .join('');
}

const AVATAR_BACKGROUNDS: Array<'primaryLow' | 'infoLow'> = ['primaryLow', 'infoLow'];

function TitleOwnerResults({
  owners,
  applicantName,
  productName,
  onConfirm,
  onTitleDisputed,
  onSelectionChange,
  isSubmitting,
}: TitleOwnerResultsProps) {
  const count = owners.length;
  const [selection, setSelection] = useState<'yes' | 'no' | ''>('');

  const handleSelection = (value: 'yes' | 'no' | '') => {
    setSelection(value);
    if (value === 'yes' || value === 'no') {
      onSelectionChange?.(value);
    }
  };

  return (
    <Stack padding="xlarge" gap="xlarge">
      <EligibilityStepHeader currentStep={3} totalSteps={5} productName={productName} />

      <Heading level="1">
        Great, we found {count} {count === 1 ? 'person' : 'people'} on the property title
      </Heading>

      <Stack gap="medium">
        {owners.map((owner, index) => (
          <Box key={owner.id} borderRadius="medium" borderWidth="standard" border="standard" padding="large">
            <Stack gap="small">
              <Box
                borderRadius="full"
                background={AVATAR_BACKGROUNDS[index] ?? 'infoLow'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ width: 40, height: 40 }}
              >
                <Text weight="semibold" size="small">
                  {getInitials(owner.fullName)}
                </Text>
              </Box>
              <Text weight="semibold">{owner.fullName}</Text>
              <Box display="flex" alignItems="center" gap="small" marginTop="small">
                <DocumentTextIcon size="xsmall" tone="muted" />
                <Text size="small" tone="muted">
                  Needs to declare taxable income.
                </Text>
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>

      <Stack gap="medium">
        <Text weight="semibold">Are these the correct owners of your property?</Text>
        <RadioGroup<'yes' | 'no' | ''> value={selection} onChange={handleSelection}>
          <Columns gap="large" collapseBelow="tablet">
            <RadioCard value="yes">Yes</RadioCard>
            <RadioCard value="no">No</RadioCard>
          </Columns>
        </RadioGroup>
      </Stack>

      {selection === 'yes' && (
        <>
          <Box
            display="flex"
            alignItems="start"
            gap="medium"
            padding="large"
            borderRadius="medium"
            background="infoMuted"
          >
            <Box flexShrink={0} paddingTop="xsmall">
              <InformationCircleIcon size="small" tone="info" />
            </Box>
            <Text size="small">
              Please note that{' '}
              <Text as="span" weight="semibold" inline>
                {applicantName}
              </Text>{' '}
              will be the person applying for the loan. The names listed above are used to check eligibility only.
            </Text>
          </Box>

          <Button tone="primary" onClick={onConfirm}>
            Continue
          </Button>
        </>
      )}

      {selection === 'no' && (
        <>
          <Box
            display="flex"
            alignItems="start"
            gap="medium"
            padding="large"
            borderRadius="medium"
            background="cautionLow"
          >
            <Box flexShrink={0} paddingTop="xsmall">
              <ExclamationIcon size="small" tone="caution" />
            </Box>
            <Text size="small">
              You can still continue. Our team will contact you to confirm who is on title and verify total household
              income is under $210,000. You&apos;ll need a Notice of Assessment for each person on title.
            </Text>
          </Box>

          <Button tone="primary" onClick={onTitleDisputed} loading={isSubmitting} disabled={isSubmitting}>
            Continue to application
          </Button>
        </>
      )}
    </Stack>
  );
}

export default TitleOwnerResults;
